import queryActionFactory from 'common/actions/queryActionFactory';
import { IdeaFieldTypes } from 'common/constants/ideas';
import DataV2 from 'common/DataV2';
import { computeScoreValues } from 'common/ideas/utils/calculateScoreFields';

import type { Idea, IdeasConfiguration, ScoreIdeaField } from 'common/api/endpoints/idea';
import type { IdeasState } from 'common/reducers/ideas';
import type { QueryState } from 'common/reducers/queryReducerFactory';
import type { Dispatch, GetState } from 'redux-connect';
export { defaultState } from 'common/reducers/queryReducerFactory';

export type QueryParams =
  | {
      ideaID: string;
    }
  | {
      ideaURLName: string;
    };

const ideasActions = queryActionFactory<Idea, QueryParams>('idea', DataV2.queries.idea);

export const {
  // query helpers
  getQueryKey,

  // action creators
  invalidateQueries: invalidateIdea,
  loadQuery: loadIdea,
  reloadQuery: reloadIdeaBase,
  bulkRemoveAction: bulkRemoveIdeas,

  // action types
  BulkRemoveType,
  BulkLoadedType,
  RequestType,
  RequestMoreType,
  LoadedType,
  LoadedMoreType,
  ErrorType,
  InvalidateType,
} = ideasActions;

export const reloadIdea = ({ ideaID }: { ideaID: string }) => {
  return async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    await dispatch(reloadIdeaBase({ ideaID }));

    const state = getState();
    const idQueryKey = getQueryKey({ ideaID });
    const ideaQuery = state.ideas?.[idQueryKey];
    const idea = ideaQuery?.items?.[0];

    // Use upsertIdeas to update both query keys
    // This ensures both ideaID and ideaURLName query keys are updated
    if (idea) {
      await dispatch(upsertIdeas([idea]));
    }
  };
};

export const upsertIdeas = (ideas: Idea[]) => {
  return async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    const state = getState();
    const allFields: IdeasConfiguration['fields'] = state.ideasConfiguration?.data?.fields || [];
    const scoreFields: ScoreIdeaField[] = allFields.filter((f) => f.type === IdeaFieldTypes.score);

    const recomputedIdeas = scoreFields.length
      ? ideas.map((idea) => {
          const scores = computeScoreValues({
            fieldsMap: idea.fields,
            scoreFields: scoreFields.map((sf) => ({ name: sf.name, fields: sf.fields })),
          });
          return {
            ...idea,
            fields: {
              ...idea.fields,
              ...scores,
            },
          } as Idea;
        })
      : ideas;

    await dispatch(
      ideasActions.bulkLoadedAction(
        recomputedIdeas
          .map((idea) => {
            const idQueryParams = {
              ideaID: idea._id,
            };
            const idQueryKey = getQueryKey(idQueryParams);
            const urlNameQueryParams = {
              ideaURLName: idea.urlName,
            };
            const urlNameQueryKey = getQueryKey(urlNameQueryParams);
            return [
              {
                queryKey: idQueryKey,
                queryParams: idQueryParams,
                queryResponse: {
                  cursor: null,
                  items: [idea],
                  hasNextPage: false,
                },
              },
              {
                queryKey: urlNameQueryKey,
                queryParams: urlNameQueryParams,
                queryResponse: {
                  cursor: null,
                  items: [idea],
                  hasNextPage: false,
                },
              },
            ];
          })
          .flat(),
      ),
    );
  };
};

export const recomputeAllIdeaScores = () => {
  return async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    const state = getState();
    const allFields: IdeasConfiguration['fields'] = state.ideasConfiguration?.data?.fields || [];
    const scoreFields: ScoreIdeaField[] = allFields.filter((f) => f.type === IdeaFieldTypes.score);

    if (scoreFields.length === 0) {
      return;
    }

    const allIdeas: Idea[] = [];
    Object.values(state.ideas)
      .filter(
        (value): value is QueryState<Idea, QueryParams> =>
          typeof value === 'object' && value !== null && 'items' in value,
      )
      .forEach((queryState) => {
        if (queryState?.items) {
          queryState.items.forEach((idea: Idea) => {
            allIdeas.push(idea);
          });
        }
      });

    // de-duplicate by _id
    const ideaById = new Map<string, Idea>();
    allIdeas.forEach((idea) => {
      ideaById.set(idea._id, idea);
    });

    await dispatch(upsertIdeas(Array.from(ideaById.values())));
  };
};

export const removeIdeas = (ideas: Idea[], ideasState: IdeasState) => {
  let allIdeasToRemove = [...ideas];

  // Find all descendant ideas that have any of the deleted ideas in their ancestorIDs
  const deletedIdeaIDs = new Set(ideas.map((idea) => idea._id));
  const descendantIdeasMap = new Map<string, Idea>();

  Object.values(ideasState)
    .filter(
      (value): value is QueryState<Idea, QueryParams> =>
        typeof value === 'object' && value !== null && 'items' in value,
    )
    .forEach((queryState) => {
      if (queryState?.items) {
        queryState.items.forEach((idea: Idea) => {
          if (
            idea.ancestorIDs &&
            idea.ancestorIDs.some((ancestorID) => deletedIdeaIDs.has(ancestorID))
          ) {
            if (!descendantIdeasMap.has(idea._id)) {
              descendantIdeasMap.set(idea._id, idea);
            }
          }
        });
      }
    });

  const descendantIdeas = Array.from(descendantIdeasMap.values());
  allIdeasToRemove = [...ideas, ...descendantIdeas];

  return ideasActions.bulkRemoveAction(
    allIdeasToRemove.map((idea) => {
      const queryParams = {
        ideaID: idea._id,
      };
      const queryKey = getQueryKey(queryParams);
      return {
        queryKey,
        queryParams,
        items: [idea],
      };
    }),
    // We know there is only one item per query, so we can delete the query if it is empty
    true,
  );
};
